/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useRef, useState, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import i18n from 'i18next';

// Component imports
import { Card, Col, Divider, Empty, Form, Input, Menu, Row, Skeleton, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import VariableSelectItem from '../../../UI/VariableSelectItem';

// Misc imports
import { VARIABLE_TYPES } from '../../../../constants/main';
import groupBy from '../../../../utils/array/groupBy';

// Constant declarations
const { Text } = Typography;

const VariableDropdown = ({
  storyVariables = [],
  botId,
  rteEditor,
  prefix = '',
  suffix = '',
  onAfterAdd = () => {},
  onHideDropdown = () => {},
  includedVariableTypes = [],
  showActivity,
  selectedVariable,
  searchTerm = '',
  isHeadlessSearch = false,
  truncateCount = 25,
  bordered = true
}) => {
  const [form] = Form.useForm();
  const [searchValue, setSearchValue] = useState('');
  const [filteredVariables, setFilteredVariables] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeMenuIndex, setActiveMenuIndex] = useState(-1);

  const inputRef = useRef(null);
  const menuWrapperRef = useRef(null);
  let lastInteractionMethod = null;

  const updateFilteredVariables = () => {
    const filtered = storyVariables
      .map(variable => {
        if (variable.type === VARIABLE_TYPES.STATIC) {
          if (variable.isConstant) {
            return { ...variable, type: VARIABLE_TYPES.CONSTANT };
          }
          return { ...variable, type: VARIABLE_TYPES.SESSION };
        }
        return variable;
      })
      .filter(variable =>
        // Remove BotName SYSTEM var
        variable.name !== 'BotName'
        && (variable.name.toLowerCase().includes(searchValue.toLowerCase()) || variable.description?.toLowerCase().includes(searchValue.toLowerCase()))
      );
    setFilteredVariables(filtered);
  };

  useEffect(() => {
    if(isHeadlessSearch) {
      if(searchTerm) {
        form.setFieldValue('variableSearch', searchTerm);
        setSearchValue(searchTerm);
      } else {
        form.setFieldValue('variableSearch', '');
        setSearchValue('');
      }
    }

  }, [searchTerm, isHeadlessSearch]);

  // Focus the search input if not headless, otherwise focus the Menu.
  useEffect(() => {
    if(!isHeadlessSearch && inputRef.current) {
      inputRef?.current?.focus({ preventScroll: true });
    }
  }, [isHeadlessSearch]);

  // Update filtered variables when storyVariables or searchValue change.
  useEffect(() => {
    updateFilteredVariables();
  }, [storyVariables, searchValue]);

  // Story variables are fetched only once in BotScreen
  // Effectively we do not have access to loading state as we always use stored data
  // Emulate loading state until the filteredVariables is set
  useEffect(() => {
    if (filteredVariables.length > 0) {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  }, [filteredVariables]);

  // Group items by type for menu display
  const groupedMenuItems = useMemo(() => {
    return Object.entries(groupBy(filteredVariables.filter((item, index, self) => self.findIndex(t => t.name === item.name) === index), 'type'))
      .filter(([key]) => [VARIABLE_TYPES.CALLBACK, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.SESSION, VARIABLE_TYPES.CONSTANT, VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.SYSTEM, VARIABLE_TYPES.CSAT, ...includedVariableTypes].includes(key)) // Keep only supported variable types
      .map(([key, value]) => ({
        type: 'group',
        label: i18n.t(key, { context: 'variable type' }),
        children: value.map(variable => {
          const itemKey = (key === VARIABLE_TYPES.CALLBACK)
            ? `${variable.name}.ADD_PROPERTY_HERE`
            : variable.name;

          return {
            label: (<div data-menu-id={itemKey}><VariableSelectItem botId={botId} variable={variable} showActivity={showActivity} truncateCount={truncateCount} showDescription={true} /></div>),
            key: itemKey
          };
        })
      }));
  }, [filteredVariables, includedVariableTypes, showActivity, truncateCount]);

  // Flatten grouped menu items for easy arrow key navigation
  const flattenedItems = useMemo(() => {
    return groupedMenuItems.flatMap((group) =>
      group.children.map(child => ({
        ...child
      }))
    );
  }, [groupedMenuItems]);

  // Scroll into view of selected variable if the list is long and the user navigates with keys
  useEffect(() => {
    if ( activeMenuIndex < 0 || activeMenuIndex >= flattenedItems.length) {
      return;
    }
    const itemKey = flattenedItems[activeMenuIndex].key;
    requestAnimationFrame(() => {
      const menuItem = menuWrapperRef.current?.querySelector(
        `[data-menu-id="${itemKey}"]`
      );
      if (menuItem) {
        menuItem.scrollIntoView({ block: 'nearest' });
      }
    });
  }, [flattenedItems, activeMenuIndex]);

  // Highlight selectedVariable if passed in prop
  useEffect(() => {
    if (!selectedVariable) {
      return;
    }

    const index = flattenedItems.findIndex(item => item.key === selectedVariable);
    setActiveMenuIndex(index);
  }, [selectedVariable, flattenedItems]);

  // Navigate variables with keyboard
  const handleOnKeyDown = (e) => {
    if (!flattenedItems.length) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveMenuIndex(prevIndex => prevIndex + 1 >= flattenedItems.length ? 0 : prevIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveMenuIndex(prevIndex => prevIndex - 1 < 0 ? flattenedItems.length - 1 : prevIndex - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleClick(flattenedItems[activeMenuIndex]?.key);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      rteEditor.focus(); // give focus back to return escape handler to parent
      onHideDropdown();
    }
  };

  const handleClick = (key) => {
    const value = `${prefix}${key}${suffix}`;
    if (rteEditor) {
      const selection = rteEditor.getSelection(true);

      if (!selection) {
        return;
      }

      // In case we have something typed while search in headless mode {{searchValue
      // we want to also replace it with the clicked value
      if(isHeadlessSearch) {
        const text = rteEditor.getText(0, selection.index);
        const lastOpenBrackets = text.lastIndexOf('{{');

        if (lastOpenBrackets !== -1) {

          //Remove the searchTerm or currently typed value after {{
          rteEditor.deleteText(lastOpenBrackets + 2, selection.index - (lastOpenBrackets + 2));

          // Insert the current clicked value
          rteEditor.insertText(lastOpenBrackets + 2, key);

          // Add }} in the end
          const textAfterVariable = rteEditor.getText(lastOpenBrackets + 2 + key.length, 2);
          if (textAfterVariable !== '}}') {
            rteEditor.insertText(lastOpenBrackets + 2 + key.length, '}}');
          }

          // Now move the cursor to the end
          rteEditor.setSelection(lastOpenBrackets + 2 + key.length + 2);
        }
      } else {

        // if clicked from the RTE toolbar just add the complete key
        const completeVariable = `{{${key}}}`;
        rteEditor.insertText(selection.index, completeVariable);
        rteEditor.setSelection(selection.index + completeVariable.length);
      }
    }

    onAfterAdd(value);
    handleClearSearch();
  };

  const handleClearSearch = () => {
    form.resetFields(['variableSearch']);
    setSearchValue('');
  };

  // Transfer focus from the input to the menu when DOWN key is pressed,
  // also handle escape if pressed before focusing menu
  const handleInputKeyDown = (e) => {
    lastInteractionMethod = 'keyboard';
    if (e.key === 'ArrowDown' && menuWrapperRef.current) {
      e.preventDefault();
      menuWrapperRef.current.focus();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      rteEditor.focus();
      onHideDropdown();
    }
  };

  // Highlight the selected variable within the menu
  let highlightedKey = null;
  if (activeMenuIndex >= 0 && activeMenuIndex < flattenedItems.length) {
    highlightedKey = flattenedItems[activeMenuIndex].key;
  } else if (selectedVariable) {
    highlightedKey = selectedVariable;
  }

  return (
    <Card bordered={bordered} className="p-0" id="variable-dropdown" style={{ width: 390, height: isHeadlessSearch ? 270 : 360 }} >
      <Form form={form}>
        <Form.Item
          hidden={isHeadlessSearch}
          name="variableSearch"
          className="px-4 pt-3 mb-2"
        >
          <Input
            ref={inputRef}
            size="large"
            placeholder={i18n.t('Search variables')}
            allowClear
            value={searchValue}
            autoFocus={false}
            prefix={<SearchOutlined className="text-muted" />}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleInputKeyDown}
          />
        </Form.Item>
      </Form>

      <Divider className="mt-4 mb-2" hidden={isHeadlessSearch}/>

      {
        isLoading ? (
          <Row gutter={18}>
            <Col xs={24}>
              <Skeleton className="p-4" loading={isLoading}/>
            </Col>
          </Row>
        ) : filteredVariables?.length > 0 ? (
          // Use a wrapper div to receive focus for easy keyboard navigation
          <div
            ref={menuWrapperRef}
            tabIndex={0} // Do not remove, makes this div interactive in order to receive focus
            onKeyDown={handleOnKeyDown}
            onMouseEnter={() => lastInteractionMethod = 'mouse'}
            onMouseLeave={() => lastInteractionMethod = null }
            onFocus={() => {
              if (activeMenuIndex === -1 && flattenedItems.length > 0 && lastInteractionMethod !== 'mouse') {
                setActiveMenuIndex(0);
              }
            }}
          >
            <Menu
              selectedKeys={highlightedKey ? [highlightedKey] : []}
              items={groupedMenuItems}
              onClick={(event) => handleClick(event.key)}
              onAfterAdd={onAfterAdd}
              mode="inline"
              inlineIndent={16}
              className={classNames('ant-menu-sm scrollbar-custom scrollbar-custom-nohide overflow-y-auto overflow-x-hidden shadow-none mb-2', {
                'border-radius-base': isHeadlessSearch
              })}
              style={{ height: 270 }}
            />
          </div>
        ) : (
          <Empty
            className="px-2 py-4 m-0"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text className="text-secondary fs-sm">{i18n.t('No variables available')}</Text>}
          />
        )
      }
    </Card>
  );
};

VariableDropdown.propTypes = {
  storyVariables: PropTypes.array,
  rteEditor: PropTypes.object,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  onAfterAdd: PropTypes.func,
  onHideDropdown: PropTypes.func,
  includedVariableTypes: PropTypes.array,
  showActivity: PropTypes.bool,
  selectedVariable: PropTypes.string,
  searchTerm: PropTypes.string,
  isHeadlessSearch: PropTypes.bool,
  truncateCount: PropTypes.bool,
  bordered: PropTypes.bool,
  botId: PropTypes.string
};

export default VariableDropdown;

